import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { useAuthStore } from "../store/authStore";
import { authService } from "../services/authService";

interface AuthContextType {
  // The context will primarily use the store, so we just provide helper methods
  login: (identifier: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    setAuth,
    setLoading,
    setError,
    logout: storeLogout,
  } = useAuthStore();

  // Initialize auth state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      
      // Wait a bit to ensure Zustand persist rehydration is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check store state after rehydration
      const currentState = useAuthStore.getState();
      
      if (currentState.accessToken && currentState.refreshToken) {
        // Token exists - validate with API
        try {
          const { userService } = await import("../services/userService");
          const userProfile = await userService.getUserProfile();
          if (userProfile) {
            const { setUser } = useAuthStore.getState();
            setUser(userProfile);
          }
        } catch (error) {
          console.error("🔒 AUTH: Auth initialization failed:", {
            error,
            timestamp: new Date().toISOString(),
            source: 'AuthContext:initialization',
            hasTokensBeforeError: !!(currentState.accessToken && currentState.refreshToken),
            errorStatus: (error as any)?.response?.status
          });
          
          // Only logout if it's not a 401 error (which will be handled by API interceptor)
          // This prevents double logout when token refresh is happening
          if ((error as any)?.response?.status !== 401) {
            storeLogout();
          }
        }
      } else {
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []); // Empty dependency array - only run on mount

  const login = async (identifier: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.login({ identifier, password });
      
      setAuth(response.user, response.accessToken, response.refreshToken);
    } catch (error: any) {
      console.error("Login failed:", error);
      let errorMessage = "Inloggen mislukt. Probeer het opnieuw.";
      
      if (error.response?.data) {
        const data = error.response.data;
        // Handle validation errors with specific field messages
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          errorMessage = data.errors.map((err: any) => err.message).join(', ');
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        }
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any): Promise<any> => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.register(userData);
      // Note: No longer auto-logging in user - they need to verify email first
      console.log("User registered successfully - verification email sent", { 
        userId: response.user.id, 
        email: response.user.email 
      });
      
      return response; // Return response so caller can handle success
    } catch (error: any) {
      let errorMessage = "Registratie mislukt. Probeer het opnieuw.";
      
      if (error.response?.data) {
        const data = error.response.data;
        // Handle validation errors with specific field messages
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          errorMessage = data.errors.map((err: any) => err.message).join(', ');
        } else if (data.message) {
          errorMessage = data.message;
        } else if (data.error) {
          errorMessage = data.error;
        }
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      storeLogout();
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await authService.forgotPassword({ email });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Versturen van reset e-mail mislukt. Probeer het opnieuw.";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (
    token: string,
    password: string,
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await authService.resetPassword({ token, password });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Wachtwoord resetten mislukt. Probeer het opnieuw.";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
